import { ValidationResult } from './validation-result.model';
import { AppState } from '../models/app-state.model';
import { validateCategory } from './category.validator';
import { validateLadderState, validateLadderLaneUniqueness } from './ladder.validator';
import { validateCard } from './card.validator';
import { validateParentHierarchy } from './parent-child.validator';
import { SCHEMA_VERSION, DEFAULT_CATEGORY_ID } from '../constants/app.constants';
import { isAppState } from '../utils/type-guards';
import { validateIsoDateTimeString } from './timestamp.validator';
import { validateUiState } from './ui-state.validator';

export function validateAppState(appState: unknown): ValidationResult {
  const errors: string[] = [];

  if (!isAppState(appState)) {
    errors.push('State format is heavily corrupted or missing critical properties.');
    return { isValid: false, errors };
  }

  if (appState.schemaVersion !== SCHEMA_VERSION) {
    errors.push(
      `Unsupported schema version. Expected ${SCHEMA_VERSION}, got ${appState.schemaVersion}.`,
    );
  }

  errors.push(...validateIsoDateTimeString(appState.savedAt, 'Saved timestamp').errors);
  errors.push(...validateUiState(appState.ui).errors);

  // Categories
  const categoryIds = new Set<string>();
  let hasUncategorized = false;

  for (const cat of appState.categories) {
    if (categoryIds.has(cat.id)) {
      errors.push(`Duplicate category ID found: ${cat.id}`);
    }
    categoryIds.add(cat.id);

    if (cat.id === DEFAULT_CATEGORY_ID) {
      hasUncategorized = true;
    }
    const catResult = validateCategory(cat);
    if (!catResult.isValid) errors.push(...catResult.errors);
  }

  if (!hasUncategorized) {
    errors.push('System Uncategorized category is missing.');
  }

  // Ladder Configuration
  const ladderResult = validateLadderState(appState.ladder);
  if (!ladderResult.isValid) errors.push(...ladderResult.errors);

  // Cards
  const cardIds = new Set<string>();
  const validCategoryIdsArr = Array.from(categoryIds);

  for (const card of appState.cards) {
    if (cardIds.has(card.id)) {
      errors.push(`Duplicate card ID found: ${card.id}`);
    }
    cardIds.add(card.id);

    const cardResult = validateCard(card, validCategoryIdsArr, appState.ladder.levels);
    if (!cardResult.isValid) {
      errors.push(`Card ${card.id || 'Unknown'} is invalid: ` + cardResult.errors.join(' '));
    }

    if (card.parentCardId !== null) {
      if (card.parentCardId === card.id) {
        errors.push(`Card ${card.id} references itself as parent.`);
      }

      if (!appState.cards.some((c) => c.id === card.parentCardId)) {
        errors.push(`Card ${card.id} references a missing parent ${card.parentCardId}.`);
      }
    }
  }

  const hierarchyResult = validateParentHierarchy(appState.cards);
  if (!hierarchyResult.isValid) {
    errors.push(...hierarchyResult.errors);
  }

  for (const level of appState.ladder.levels) {
    const cardsInLevel = appState.cards.filter((c) => c.showInLadder && c.priority === level);
    const laneResult = validateLadderLaneUniqueness(cardsInLevel);
    if (!laneResult.isValid) {
      errors.push(`Lane uniqueness broken on level ${level}: ` + laneResult.errors.join(' '));
    }
  }

  return { isValid: errors.length === 0, errors };
}
